// src/components/discovery/DropCard.jsx
//
// Extracted out of Discover.jsx so the new full "See all" pages can
// render the exact same card as the Discover rails — same markup,
// same classes, no visual drift between preview and full page.

import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ProfileAvatarLink from "../profile/ProfileAvatarLink";
import ProfileNameLink from "../profile/ProfileNameLink";
import { getNicheMeta, NicheIcon } from "../niche";
import { formatDropPrice, dropUnlockCtaLabel, isDropFree } from "../../utils/dropPricing";
import { viewsLeft, pctViewed, thumbnailUrl } from "../../utils/discoveryPools";
import {
  DURATION,
  EASE,
  STAGGER_DELAY,
  STAGGER_CAP,
  tapPress,
  tapPressTransition,
  useMotionSafe,
} from "../../utils/motionTokens";

const NICHE_ACCENTS = {
  wellness: "linear-gradient(90deg,#C4790A,#d49010)",
  beauty: "linear-gradient(90deg,#6B4FA8,#a070e0)",
  food: "linear-gradient(90deg,#1A8C52,#30c870)",
  fitness: "linear-gradient(90deg,#D4520A,#f07030)",
  auto: "linear-gradient(90deg,#607080,#8090a0)",
  mind: "linear-gradient(90deg,#4A5280,#6a72a0)",
  arts: "linear-gradient(90deg,#9B3A3A,#b05050)",
  independent: "linear-gradient(90deg,#C4790A,#d49010)",
};

export default function DropCard({ drop, creator, access, compact = false, isPaid = false, index = 0 }) {
  const { prefersReduced } = useMotionSafe();
  const left = viewsLeft(drop);
  const pct = pctViewed(drop);
  const isClosed = access.type === "closed";
  const urgent = !isClosed && left <= 15;
  const meta = getNicheMeta(drop.niche);
  const accent = NICHE_ACCENTS[meta.key] || NICHE_ACCENTS.independent;

  const unlocked =
    access.type === "free" ||
    access.type === "paid-unlocked" ||
    access.type === "sub-unlocked";
  const ctaLabel = dropUnlockCtaLabel(drop, { closed: isClosed, unlocked });
  const priceLabel = isClosed ? "—" : formatDropPrice(drop);

  // Scroll-entrance: fade + slight rise, staggered by position in its
  // list (capped so far-down items in a long grid don't queue behind
  // a long delay — see motionTokens.STAGGER_CAP). Skipped entirely
  // under prefers-reduced-motion.
  const entranceDelay = Math.min(index, STAGGER_CAP) * STAGGER_DELAY;
  const entranceProps = prefersReduced
    ? {}
    : {
        initial: { opacity: 0, y: 14 },
        whileInView: {
          opacity: 1,
          y: 0,
          transition: { duration: DURATION.entrance, ease: EASE.out, delay: entranceDelay },
        },
        viewport: { once: true, amount: 0.3 },
      };

  return (
    <motion.article
      className={`gal-dc${compact ? " gal-dc--rail" : ""}`}
      {...entranceProps}
      whileTap={prefersReduced ? undefined : { ...tapPress, transition: tapPressTransition }}
    >
      <div className="gal-dc-accent" style={{ background: accent }} />
      <Link to={`/drop/${drop.id}`} className="gal-dc-thumb">
        <img src={thumbnailUrl(drop.thumbnailUrl)} alt="" loading="lazy" />
        <div className="gal-dc-shade" />
        {/* Honest label, not "Sponsored" — the creator paid to feature
            their own content, deliberately styled neutral gray (not
            gold) so it never reads as editorial or earned. Reuses the
            existing .gal-dc-type class, previously unused. */}
        {isPaid && <div className="gal-dc-type">Paid Spot</div>}
        {!isClosed && (
          <div className="gal-dc-chip">
            <span className={`gal-dcn ${urgent ? "warn" : "open"}`}>{drop.viewCount || 0}</span>
            <span className="gal-dct">
              <strong>of {drop.maxViews || 100}</strong>
              {urgent ? "closing ⚡" : `${left} remain`}
            </span>
          </div>
        )}
      </Link>
      <div className="gal-dc-scar">
        <div className="gal-dcs-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="gal-dc-body">
        <div className="gal-dc-creator-row">
          <ProfileAvatarLink
            user={creator}
            src={creator?.avatarUrl || creator?.avatar}
            alt={creator?.displayName || "Creator"}
            className="gal-dc-av-link"
            imgClassName="gal-dc-av"
          />
          <ProfileNameLink user={creator} className="gal-dc-cname">
            {creator?.displayName || "Creator"}
          </ProfileNameLink>
          <span className={`gal-dc-ntag gal-dnt-${meta.key}`}>
            <NicheIcon niche={meta.key} size={10} />
            {meta.browseLabel || meta.label}
          </span>
        </div>
        <Link to={`/drop/${drop.id}`} className="gal-dc-title">
          {drop.title}
        </Link>
        <div className="gal-dc-foot">
          {!isClosed ? (
            <>
              <span className={`gal-dc-pill ${urgent ? "gal-dcp-warn" : "gal-dcp-open"}`}>
                {urgent && <span className="gal-dcp-dot" />}
                {drop.viewCount || 0}/{drop.maxViews || 100}
                {urgent ? " ⚡" : ""}
              </span>
              <span className="gal-dc-price">{priceLabel}</span>
              {ctaLabel && (
                <Link
                  to={`/drop/${drop.id}`}
                  className={`gal-dc-unlock${isDropFree(drop) || unlocked ? " gal-dc-unlock--ghost" : ""}`}
                >
                  {ctaLabel}
                </Link>
              )}
            </>
          ) : (
            <>
              <span className="gal-dc-pill gal-dcp-open">Closed</span>
              <span className="gal-dc-price">—</span>
            </>
          )}
        </div>
      </div>
    </motion.article>
  );
}